import { Route, Switch, Router as WouterRouter, useLocation } from 'wouter';
import { motion } from 'framer-motion';
import { lazy, Suspense, useEffect } from 'react';
import { initAnalytics, initScrollDepthTracking, initTimeOnPageTracking, track404, trackPageView } from './services/analytics';
import { applySeoMeta } from './services/seo';
import { servicePageMap } from './services/servicePages';

import CustomCursor from './components/CustomCursor';
import Navbar from './sections/Navbar';
import Hero from './sections/Hero';

const Services = lazy(() => import('./sections/Services'));
const WhyChooseUs = lazy(() => import('./sections/WhyChooseUs'));
const Pricing = lazy(() => import('./sections/Pricing'));
const Portfolio = lazy(() => import('./sections/Portfolio'));
const Industries = lazy(() => import('./sections/Industries'));
const Process = lazy(() => import('./sections/Process'));
const AiAutomation = lazy(() => import('./sections/AiAutomation'));
const Testimonials = lazy(() => import('./sections/Testimonials'));
const Faq = lazy(() => import('./sections/Faq'));
const ContactFooter = lazy(() => import('./sections/ContactFooter'));
const ServicePage = lazy(() => import('./pages/ServicePage'));

function NotFound() {
  useEffect(() => {
    applySeoMeta({
      title: 'Page Not Found | Orbitra Solution',
      description: 'The requested Orbitra Solution page could not be found.',
      noindex: true,
    });
    track404();
  }, []);

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#050505] text-white">
      <main className="text-center" aria-label="Page not found">
        <p className="text-8xl font-serif font-bold" aria-hidden="true">404</p>
        <h1 className="sr-only">Page Not Found - Orbitra Solution</h1>
        <p className="mt-4 text-xl font-light text-gray-400">
          This digital space does not exist.
        </p>
        <a href="/" className="interactive mt-8 inline-block border-b border-white pb-1 hover:text-gray-300 transition-colors" aria-label="Return to Orbitra Solution homepage">
          Return to Base
        </a>
      </main>
    </div>
  );
}

function SectionFallback({ className = 'min-h-[420px] bg-[#050505]' }: { className?: string }) {
  return <div className={className} aria-hidden="true" />;
}

function Home() {
  useEffect(() => {
    applySeoMeta({ path: '/' });
  }, []);

  return (
    <div className="bg-[#050505] min-h-screen w-full font-sans text-white overflow-x-hidden">
      <CustomCursor />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="relative overflow-x-hidden"
      >
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[10000] focus:px-6 focus:py-3 focus:bg-white focus:text-black focus:text-xs focus:uppercase focus:tracking-widest focus:font-semibold focus:rounded-full focus:shadow-[0_0_24px_rgba(255,255,255,0.25)] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black transition-all duration-300"
        >
          Skip to main content
        </a>

        <header aria-label="Orbitra Solution site navigation">
          <Navbar />
        </header>

        <main id="main-content" tabIndex={-1} className="focus:outline-none overflow-x-hidden" aria-label="Orbitra Solution main content">
          <Hero />
          <Suspense fallback={<SectionFallback />}>
            <div className="content-auto"><Services /></div>
            <div className="content-auto"><WhyChooseUs /></div>
            <div className="content-auto"><Pricing /></div>
            <div className="content-auto"><Portfolio /></div>
            <div className="content-auto"><Industries /></div>
            <div className="content-auto"><Process /></div>
            <div className="content-auto"><AiAutomation /></div>
            <div className="content-auto"><Testimonials /></div>
            <div className="content-auto"><Faq /></div>
          </Suspense>
        </main>

        <Suspense fallback={<SectionFallback className="min-h-[640px] bg-[#050505]" />}>
          <footer>
            <ContactFooter />
          </footer>
        </Suspense>
      </motion.div>
    </div>
  );
}

function Router() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/services/:slug" component={ServiceRoute} />
      <Route component={NotFound} />
    </Switch>
  );
}

function ServiceRoute() {
  const slug = window.location.pathname.split('/').filter(Boolean).pop() ?? '';
  const page = servicePageMap.get(slug);
  if (!page) return <NotFound />;
  return (
    <Suspense fallback={<SectionFallback className="min-h-screen bg-[#050505]" />}>
      <ServicePage page={page} />
    </Suspense>
  );
}

function AnalyticsRouteObserver() {
  const [location] = useLocation();

  useEffect(() => {
    initAnalytics();
    const cleanupScroll = initScrollDepthTracking();
    const cleanupTime = initTimeOnPageTracking();
    return () => {
      cleanupScroll();
      cleanupTime();
    };
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => trackPageView(location), 0);
    return () => window.clearTimeout(timer);
  }, [location]);

  return null;
}

function App() {
  return (
    <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, '')}>
      <AnalyticsRouteObserver />
      <Router />
    </WouterRouter>
  );
}

export default App;
